import { useState, useEffect } from 'react';
import { ensureMinLoading } from '../Utils/withLoading';
import {
  IconMugFilled, IconGlassFullFilled, IconBreadFilled, IconBowlSpoonFilled, IconCookieFilled,
  IconMilkFilled, IconSaladFilled, IconPizzaFilled, IconBottleFilled,
} from '@tabler/icons-react';
import ModalEliminarCategoria from './ModalEliminarCategoria';
import { apiFetch } from '../api/client';

const FONT = '"Public Sans", sans-serif';

const inputClass =
  'w-full outline-none bg-white/[0.08] focus:bg-white/[0.12] focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)] placeholder:text-white/30 transition-all duration-200 ease-out';

const inputStyle = {
  border: 'none',
  borderRadius: '16px',
  height: '48px',
  padding: '0 16px',
  fontSize: '15px',
  fontWeight: 500,
  fontFamily: FONT,
  color: 'rgba(255, 255, 255, 0.95)',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: '13px',
  lineHeight: '18px',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  fontFamily: FONT,
};

const presetColors = [
  '#F97316', '#EA580C', '#22D3EE', '#0891B2', '#34D399', '#059669', '#F472B6', '#DB2777',
  '#A78BFA', '#7C3AED', '#FBBF24', '#D97706', '#60A5FA', '#2563EB', '#FB7185', '#BE185D',
];

const presetIcons = [
  { component: IconMugFilled, label: 'Coffee' },
  { component: IconGlassFullFilled, label: 'Cup' },
  { component: IconBreadFilled, label: 'Cake' },
  { component: IconBowlSpoonFilled, label: 'Meat' },
  { component: IconCookieFilled, label: 'Cookie' },
  { component: IconMilkFilled, label: 'Ice cream' },
  { component: IconSaladFilled, label: 'Salad' },
  { component: IconPizzaFilled, label: 'Pizza' },
  { component: IconBottleFilled, label: 'Bottle' },
];

export default function ModalEditarCategoria({ isOpen, onClose, onUpdated, category, dynamicIslandRef }) {
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    color: presetColors[0],
    icono: '',
  });
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isOpen && category) {
      setForm({
        nombre: category.name || '',
        descripcion: category.description || '',
        color: category.color || presetColors[0],
        icono: category.icon || '',
      });
      setSaving(false);
      setShowDeleteModal(false);
    }
  }, [isOpen, category]);

  if (!isOpen || !category) return null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) return;

    setSaving(true);
    dynamicIslandRef?.current?.addAlert('loading', 'Actualizando categoría...');
    const startedAt = Date.now();

    try {
      const body = {};
      if (form.nombre.trim() !== category.name) body.name = form.nombre.trim();
      if (form.descripcion.trim() !== (category.description || '')) body.description = form.descripcion.trim() || null;
      if (form.color !== category.color) body.color = form.color;
      if (form.icono !== (category.icon || '')) body.icon = form.icono || null;

      if (Object.keys(body).length === 0) {
        dynamicIslandRef?.current?.clearAlerts();
        onUpdated?.();
        return;
      }

      const res = await apiFetch(`/categories/${category.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let msg = 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.';
        try {
          const err = await res.json();
          if (err.detail) msg = err.detail;
          else if (Array.isArray(err.detail)) msg = err.detail.map(d => d.msg).join(', ');
        } catch {}
        dynamicIslandRef?.current?.clearAlerts();
        dynamicIslandRef?.current?.addAlert('error', msg);
        return;
      }

      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Categoría actualizada correctamente');
      onUpdated?.();
    } catch (e) {
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async () => {
    setSaving(true);
    dynamicIslandRef?.current?.addAlert('loading', 'Eliminando categoría...');
    const startedAt = Date.now();
    try {
      const res = await apiFetch(`/categories/${category.id}`, { method: 'DELETE' });
      if (!res.ok) {
        dynamicIslandRef?.current?.clearAlerts();
        dynamicIslandRef?.current?.addAlert('error', 'Error al eliminar la categoría');
        return;
      }
      await ensureMinLoading(startedAt);
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('success', 'Categoría eliminada correctamente');
      setShowDeleteModal(false);
      onUpdated?.();
    } catch {
      dynamicIslandRef?.current?.clearAlerts();
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = form.nombre.trim() && !saving;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-3"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.72)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={onClose}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="editar-categoria-titulo"
          className="flex flex-col overflow-hidden w-full"
          style={{
            backgroundColor: '#000',
            borderRadius: '28px',
            border: '1px solid rgba(255, 255, 255, 0.16)',
            maxWidth: '820px',
            maxHeight: 'min(640px, calc(100dvh - 24px))',
            boxShadow: '0 40px 120px rgba(0, 0, 0, 0.8)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-white/[0.16]">
            <h2
              id="editar-categoria-titulo"
              className="font-semibold m-0 truncate"
              style={{
                fontSize: '22px',
                lineHeight: '28px',
                letterSpacing: '-0.02em',
                color: 'rgba(255, 255, 255, 0.95)',
                fontFamily: FONT,
              }}
            >
              Editar categoría
            </h2>
            <p
              className="m-0 mt-1 truncate"
              style={{
                fontSize: '13px',
                lineHeight: '18px',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 0.45)',
                fontFamily: FONT,
              }}
            >
              {category.name} · Color e icono
            </p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 sm:px-6 py-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <label htmlFor="editar-categoria-nombre" style={labelStyle}>Nombre *</label>
                  <input
                    id="editar-categoria-nombre"
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Ej: Bebidas calientes, Snacks"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="editar-categoria-descripcion" style={labelStyle}>Descripción</label>
                  <textarea
                    id="editar-categoria-descripcion"
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Describe la categoría..."
                    rows={3}
                    className={`${inputClass} resize-y`}
                    style={{
                      ...inputStyle,
                      borderRadius: '18px',
                      height: 'auto',
                      padding: '14px 16px',
                      minHeight: '108px',
                    }}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <span style={labelStyle}>Color del badge</span>
                  <div className="grid grid-cols-8 gap-2">
                    {presetColors.map((c) => {
                      const selected = form.color === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() => handleChange('color', c)}
                          aria-label={`Color ${c}`}
                          aria-pressed={selected}
                          className="w-full aspect-square rounded-full border-none cursor-pointer touch-manipulation transition-transform duration-200 ease-out active:scale-95"
                          style={{
                            backgroundColor: c,
                            transform: selected ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: selected
                              ? '0 0 0 2px #000, 0 0 0 4px rgba(255, 255, 255, 0.9)'
                              : '0 0 0 1px rgba(255, 255, 255, 0.08)',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span style={labelStyle}>Icono</span>
                  <div className="flex flex-wrap gap-2">
                    {presetIcons.map(({ component: IconComp, label }) => {
                      const selected = form.icono === label;
                      return (
                        <button
                          key={label}
                          type="button"
                          onClick={() => handleChange('icono', label)}
                          aria-label={`Icono ${label}`}
                          aria-pressed={selected}
                          className="flex items-center justify-center w-11 h-11 rounded-2xl border-none cursor-pointer touch-manipulation transition-colors duration-200 ease-out active:scale-[0.96] [@media(hover:hover)]:hover:bg-white/[0.1]"
                          style={{
                            backgroundColor: selected ? '#fff' : 'rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          <IconComp size={19} color={selected ? '#000' : 'rgba(255, 255, 255, 0.7)'} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0 px-4 sm:px-6 py-4 border-t border-white/[0.16]">
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              disabled={saving}
              className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:brightness-110"
              style={{
                backgroundColor: '#ff453a',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: '#fff',
                fontFamily: FONT,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              Eliminar
            </button>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="flex items-center justify-center h-12 px-6 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-white/[0.85]"
                style={{
                  backgroundColor: '#fff',
                  fontSize: '14px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: '#000',
                  fontFamily: FONT,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
                className="flex items-center justify-center h-12 px-7 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.98] [@media(hover:hover)]:hover:bg-[#0077ed]"
                style={{
                  backgroundColor: canSave ? '#0071e3' : 'rgba(255, 255, 255, 0.06)',
                  fontSize: '15px',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  fontFamily: FONT,
                  color: canSave ? '#fff' : 'rgba(255, 255, 255, 0.35)',
                  cursor: canSave ? 'pointer' : 'not-allowed',
                }}
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalEliminarCategoria
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteCategory}
        categoryName={category.name}
      />
    </>
  );
}
